import { Menu, MenuEntree, MenuItem, PortionItemType, MenuItemType, MenuItemGroup, PreConfiguredMeal, MealType, MenuItemLookupResult, OrderMeal, OrderWithContext, RecentOrFavoriteOrderMeal, MenuNonFoodItem } from '@/models';
import { freezeArray } from '.';
import { freezeObject, isDefined } from './util';
import head from 'lodash/head';
import { some } from 'lodash';

export function findEntreeInMenu(orderEntreeId: string, menu: Menu): MenuEntree | undefined {
  return (menu.entrees || []).find(e => e.itemId == orderEntreeId);
}

/** This does not search inside Entree.Contents for items */
export function findItemInMenu(itemId: string, menu: Menu): MenuItem | undefined {
  return getAllMenuItems(menu).find(i => i.itemId == itemId);
}

export function getAllMenuItems(menu: Menu, includeKidsSidesAndDrinks: boolean = false, includeUniqueEntreeItems: boolean = false): MenuItem[] {
  let allMenuItems = (<readonly MenuItem[]>(menu.entrees ?? [])).concat(head(menu.entrees)?.contents ?? []).concat(menu.sides ?? []).concat(menu.drinks ?? []).concat(menu.nonFoodItems ?? [] as any);

  if (includeKidsSidesAndDrinks) {
    allMenuItems = allMenuItems.concat(getKidsSidesAndDrinks(menu));
  }

  if (includeUniqueEntreeItems) {
    allMenuItems = allMenuItems.concat(getUniqueeEntreeItems(menu, allMenuItems));
  }

  return allMenuItems;
}

export function getKidsSidesAndDrinks(menu: Menu): MenuItem[] {
  // Just grabbing the first kids entree. All kids entrees share the same sides and drinks
  const kidsEntree = menu.entrees?.filter(e => e.itemType === 'KidsBYO' || e.itemType === 'KidsQuesadilla')[0];

  return kidsEntree?.contents?.filter(c => c.itemType === 'Beverage' || c.itemType === 'Side') ?? [];
}

// This should get items that exist in specific entrees (i.e, corn tortilla in tacos, honey vinaigrette in salads, etc.)
export function getUniqueeEntreeItems(menu: Menu, allMenuItems: MenuItem[]): MenuItem[] {
  const allEntreeContents = menu.entrees?.flatMap(e => e.contents);

  if (allEntreeContents) {
    const distinctEntreeContents = [...new Map(allEntreeContents.map(c => [c?.itemId, c])).values()].filter(isDefined);
    return distinctEntreeContents.filter(c => !allMenuItems.find(mi => mi.itemId == c?.itemId));
  }

  return [];
}

export function getPortionItemType(item: MenuItem): PortionItemType | undefined {
  return isPortionItemType(item.itemType) ? item.itemType : undefined;
}

export function isPortionItemType(type: MenuItemType): type is PortionItemType {
  const portionItemType = type as PortionItemType;

  return portionItemType == 'ExtraPortion' || portionItemType == 'HalfPortion';
}

export function freezeMenu<T extends Menu | undefined>(menu: T): T {
  if (!menu) {
    return menu;
  }

  freezeArray(menu.entrees || []);
  freezeArray(menu.sides || []);
  freezeArray(menu.drinks || []);
  freezeObject(menu as Menu);

  return menu;
}

export function isBYOGroupAvailable(group: MenuItemGroup, preconfiguredMeals: PreConfiguredMeal[], isCountryUK: boolean, menuItemLookupMap: Map<string, MenuItemLookupResult> | null): boolean {
  const byoMeals = preconfiguredMeals.filter(pcm => pcm.mealType == MealType.BuildYourOwn && !isCountryUK);
  return some(byoMeals, byom => menuItemLookupMap?.get(byom.entree.itemId)); 
}

// Hardcoding top level menu id for BYO group in order to differentiate from other PCEs.
// This should be a temporary solution until we get BYOFM out the door.
export function isGroupBYO(group: MenuItemGroup): boolean {
  const byoTopLevelMenuIds = ['4dedd7da-3b33-4f63-acc4-ce1da5dfe81a',  // BYO PCE
                              '9bec1d5b-2ff9-4947-b4ac-8102fcdd6483',  // BYO Protein Builder
                              '380e95c2-346a-4827-a632-0eaebe0738ec']; // BYO Chips Builder
  return byoTopLevelMenuIds.indexOf(group.id) > -1;
}

export function isMealBYO(meal: OrderMeal | RecentOrFavoriteOrderMeal, activeMenu: Menu | null): boolean {
  return  meal?.entrees?.some(e => activeMenu?.entrees?.some(a => (a.itemType == 'BYOProtein' || a.itemType == 'BYOChips') && e.menuItemId == a.itemId)) || false;
}

export function isMealBYOProtein(meal: OrderMeal, activeMenu: Menu | null): boolean {
  return meal?.entrees?.some(e => activeMenu?.entrees?.some(a => a.itemType == 'BYOProtein' && e.menuItemId == a.itemId)) || false;
}

export function isPceBYOProtein(meal: PreConfiguredMeal, activeMenu: Menu | null): boolean {
  return activeMenu?.entrees?.some(e => e.itemType == 'BYOProtein' && meal.entree.itemId == e.itemId) || false;
}

export function orderContainsBYOProtein(order: OrderWithContext, activeMenu: Menu | null): boolean {
  return order?.order?.meals?.some(m => isMealBYOProtein(m, activeMenu)) || false;
}

export function sortMenuNonFoodItems(nonFoodItems?: MenuNonFoodItem[] | null): MenuNonFoodItem[] {
  return [...(nonFoodItems || [])].sort((a, b) => {
    const firstSplit = a.itemId.split('-');
    const secondSplit = b.itemId.split('-');

    if (firstSplit.length == 2 && secondSplit.length == 2) {
      return Number.parseInt(firstSplit[1], 10) - Number.parseInt(secondSplit[1], 10);
    }

    return -1;
  });
}